import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { parse } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { type IModalLoadingRef } from '@/components/ModalLoading';
import { useGlobalContext } from '@/context/global.context';
import { useSolicitudContext } from '@/context/solicitud.context';
import { getSolicitudDetalle, type ISolicitudDetallePayload } from '@/services/solicitudes.service';

import { FORMAT_DATE } from '@/constants/date.constants';
import {
	defaultValues,
	filterDocumentosAdicionales,
	filterDocumentosRequeridos,
	filterSujetosAdicionales,
	filterSujetosRequeridos,
	getAllEntitiesRequireds,
	mapGroupDocumentos,
	mapGroupSujetos,
} from '@/helpers/formSolicitudes';
import { notificationsConfig } from '@/helpers/notifications';
import {
	type IPreSeleccionNotario,
	type FormValues,
	type IDocumentoFormGroup,
	type ISujetoGroup,
	type ISujetoListado,
	type ITramiteTMU,
} from '@/interfaces/model/don.interface';
import { type TipoDocumento, type TipoIntervencion } from '@/interfaces/model/maestros.interface';
import { getID } from '@/utils/id';
import { parseJsonObj } from '@/utils/string/string';

import useApiData from './useApiData';
import useRouteTramite from './useRouteTramite';

export const useLoadDraft = (id?: number) => {
	const navigate = useNavigate();
	const ROUTES_TRAMITE = useRouteTramite();
	const { user, maestrosTramite } = useGlobalContext();

	const [isLoading, setIsLoading] = useState(false);
	const [hasData, setHasData] = useState(false);
	const refLoading = useRef<IModalLoadingRef>(null);

	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { triggerFetch } = useApiData({ loading: ['getDraft'] as const });
	const { reset } = useFormContext<FormValues>();
	const { setData } = useSolicitudContext();

	/**
	 * Inicializa los tipo de intervención requeridos con arrays de ítems (sujetos) vacíos
	 * según los tipos de intervencion obligatorios definidos en los maestros.
	 */
	const getDefaultSujetosRequeridos = (): ISujetoGroup[] => {
		const allSujetosRequeridos = getAllEntitiesRequireds(maestrosTramite.tipoIntervencion, true);
		return allSujetosRequeridos.map((tipoIntervencion) => ({
			tipoIntervencion,
			items: [],
		}));
	};

	/**
	 * Inicializa los grupos de tipo de documentos requeridos con arrays de ítems (documentos) vacíos
	 * según los tipos de documento obligatorios definidos en los maestros.
	 */
	const getDefaultDocumentosRequeridos = (): IDocumentoFormGroup[] => {
		const allDocumentosRequeridos = getAllEntitiesRequireds(maestrosTramite.tipoDocumento, true);
		return allDocumentosRequeridos.map((tipoDocumento) => ({
			tipoDocumento,
			items: [],
		}));
	};

	// Añadir los tipo de documentos por defecto requeridos que nose hayan completado en el guardado
	const mergeDocumentosRequeridos = (
		currentDocs: IDocumentoFormGroup[],
		defaultDocs: IDocumentoFormGroup[],
	): IDocumentoFormGroup[] =>
		defaultDocs.map((defaultDoc) => {
			const matchingCurrentDoc = currentDocs.find(
				(current) => current.tipoDocumento.id === defaultDoc.tipoDocumento.id,
			);
			return matchingCurrentDoc ?? defaultDoc;
		});

	// Añadir los tipo de intervencion por defecto requeridos que nose hayan completado en el guardado
	const mergeSujetosRequeridos = (currentDocs: ISujetoGroup[], defaultDocs: ISujetoGroup[]): ISujetoGroup[] =>
		defaultDocs.map((defaultDoc) => {
			const matchingCurrentDoc = currentDocs.find(
				(current) => current.tipoIntervencion.id === defaultDoc.tipoIntervencion.id,
			);
			return matchingCurrentDoc ?? defaultDoc;
		});

	const mapToSujetosWithId = (sujetos: ISujetoListado[]) =>
		sujetos.map((item) => ({
			...item,
			...getID(item.id),
			representantes: item.representantes?.map((rep) => ({
				...rep,
				...getID(rep.id),
			})),
		}));

	const loadData = useCallback(
		async (
			payload: ISolicitudDetallePayload,
			maestros: { tipoDocumento: TipoDocumento[]; tipoIntervencion: TipoIntervencion[] },
		) => {
			setIsLoading(true);

			try {
				const response = await triggerFetch({
					fetch: getSolicitudDetalle,
					payload,
					name: 'getDraft',
				});

				let parseData: ITramiteTMU | undefined;
				if (response?.data) {
					const { dataBorrador, ...rest } = response.data;
					parseData = parseJsonObj<ITramiteTMU>(response.data.dataBorrador);
					setData(rest);
				}

				if (parseData) {
					const { documentos = [], fechaVencimiento, sujetos, ...rest } = parseData;

					const documentosRequeridos = filterDocumentosRequeridos(
						documentos,
						maestros.tipoDocumento,
						true,
					).map((item) => ({
						...item,
						...getID(item.id),
					}));

					const documentosRequeridosGrouped = mergeDocumentosRequeridos(
						mapGroupDocumentos(documentosRequeridos),
						getDefaultDocumentosRequeridos(),
					);

					const documentosAdicionales = filterDocumentosAdicionales(
						documentos,
						maestros.tipoDocumento,
						false,
					).map((item) => ({
						...item,
						...getID(item.id),						
					}));

					const sujetosMapped = mapToSujetosWithId(sujetos);
					const sujetosAdicionales = filterSujetosAdicionales(sujetosMapped, maestros.tipoIntervencion, true);
					const sujetosRequeridos = filterSujetosRequeridos(sujetosMapped, maestros.tipoIntervencion, true);

					const sujetosRequeridosGrouped = mergeSujetosRequeridos(
						mapGroupSujetos(sujetosRequeridos),
						getDefaultSujetosRequeridos(),
					);

					const { data } = response;					
					 
					const notario = data?.preSeleccionNotario as IPreSeleccionNotario | undefined;
					// --- Mapeo al Formulario ---
					reset({
						...rest,
						sujetosAdicionales,
						notarioSeleccionado: notario?.codigoCatastro && notario?.cuv ? `${ notario?.codigoCatastro };${ notario?.cuv }` : undefined,
						firmaTelematica: rest.firmaTelematica ?? false,
						sujetosRequeridos: sujetosRequeridosGrouped,
						documentosAdicionales,
						documentosRequeridos: documentosRequeridosGrouped,
						fechaVencimiento: fechaVencimiento ? parse(fechaVencimiento, FORMAT_DATE.FRONT, new Date()) : undefined,
						politicaAceptada: [],
					});

					setHasData(true);
				} else {
					throw new Error('No data');
				}
			} catch (error) {
				openNotification(notificationTemplate.errorMessage('Error al obtener el borrador'));
				navigate(ROUTES_TRAMITE.BORRADORES);
			} finally {
				setIsLoading(false);
				refLoading.current?.close();
			}
		},
		[navigate, reset, triggerFetch, ROUTES_TRAMITE, notificationTemplate, openNotification],
	);

	const loadSetted = () => {
		if (!user || !id) return;

		loadData(
			{
				nifEmisor: user.nifEmisor,
				idTipoSector: user.tipoSectorEmisor.id,
				id,
			},
			{
				tipoDocumento: maestrosTramite.tipoDocumento,
				tipoIntervencion: maestrosTramite.tipoIntervencion,
			},
		);
	};

	useEffect(() => {
		if (maestrosTramite.tipoDocumento.length > 0 && maestrosTramite.tipoIntervencion.length > 0) {
			loadSetted();
		}
	}, [id, maestrosTramite.tipoDocumento, maestrosTramite.tipoIntervencion]);

	useEffect(() => {
		if (maestrosTramite.tipoDocumento.length > 0 && maestrosTramite.tipoIntervencion.length > 0 && !id) {
			reset({
				...defaultValues(),
				documentosRequeridos: getDefaultDocumentosRequeridos(),
				sujetosRequeridos: getDefaultSujetosRequeridos(),
			});
		}
	}, [maestrosTramite.tipoDocumento, maestrosTramite.tipoIntervencion]);

	return {
		isLoading,
		hasData,
		reload: loadSetted,
	};
};
